import React from "react";
import { SectionProps } from "../Sections/SectionProps";
import { Controller } from "react-hook-form";
import { ExpertsApplyModel } from "../../domain/ExpertsApplyFormModel";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
} from "@material-ui/core";
import { nameof } from "ts-simple-nameof";
import { Select } from "@ofqual/portal-components/components/Select";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { usersService } from "../../services";

import { Checkbox } from "@ofqual/portal-components/components/Checkbox";
import { UserRole } from "../../domain/userRole";
import { useSelector } from "react-redux";

import { authSelector } from "@ofqual/portal-activity-feed/activityfeed/redux/selectors";

export const OfqualDetails: React.FC<{
  sectionProps: SectionProps<ExpertsApplyModel>;
  specialist: ExpertsApplyModel;
}> = ({ sectionProps }) => {
  const { control } = sectionProps;
  const currentAuth = useSelector(authSelector);
  const { roles } = useSelector(authSelector);
  const canAssign: boolean =
    roles.includes(UserRole.OfqualExpertsEvaluator) ||
    roles.includes(UserRole.OfqualExpertsAdmin) ||
    roles.includes(UserRole.ITAdmin);

  let isNotAnAdmin = true;
  if (
    currentAuth.roles.includes(UserRole.ITAdmin) ||
    currentAuth.roles.includes(UserRole.OfqualExpertsAdmin)
  ) {
    isNotAnAdmin = false;
  }

  let isNotAnEvaluator = true;
  if (
    currentAuth.roles.includes(UserRole.OfqualExpertsEvaluator) ||
    currentAuth.roles.includes(UserRole.ITAdmin)
  ) {
    isNotAnEvaluator = false;
  }

  const [userLookups] = useLookup({
    service: usersService,
    id: "upn",
    label: (item) => (item.fullName ? item.fullName : item.upn),
    params: {
      active: true,
    },
  });

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="rightToWork"
          render={(props) => (
            <FormControlLabel
              control={
                <Checkbox
                  onChange={(e) => {
                    props.onChange(e.target.checked);
                  }}
                  checked={props.value}
                  data-test="right-to-work-checkbox"
                  disabled={isNotAnAdmin}
                />
              }
              label="Right to work requested"
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Controller
          control={control}
          name={"rightToWorkValid"}
          render={(props) => (
            <FormControl component="fieldset" disabled={isNotAnAdmin}>
              <FormLabel id="rightToWorkValid-label" component="legend">
                Does the applicant have the right to work with Ofqual?
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="rightToWorkValid-label"
                name={"rightToWorkValid"}
                data-test="radiogroup-rightToWorkValid"
                onChange={(e) => {
                  props.onChange(e.target.value === "true");
                }}
              >
                <FormControlLabel
                  value={true}
                  label="Yes"
                  control={<Radio />}
                  data-test="rightToWorkValid-yes"
                  checked={props.value === true}
                />
                <FormControlLabel
                  value={false}
                  label="No"
                  control={<Radio />}
                  data-test="rightToWorkValid-no"
                  checked={props.value === false}
                />
              </RadioGroup>
            </FormControl>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Controller
          control={control}
          name={nameof<ExpertsApplyModel>((x) => x.assignedTo)}
          render={({ value, onChange }) => (
            <Select
              label="Assigned to"
              datatest="assigned-users-field"
              name={nameof<ExpertsApplyModel>((x) => x.assignedTo)}
              selectVariant={"multiple"}
              value={value}
              disabled={!canAssign}
              onChange={(e) => onChange(e.target.value)}
            >
              {userLookups}
            </Select>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Controller
          control={control}
          name={"referencesRequested"}
          render={(props) => (
            <FormControlLabel
              control={
                <Checkbox
                  onChange={(e) => {
                    props.onChange(e.target.checked);
                  }}
                  checked={props.value}
                  data-test="reference-checkbox"
                  name={"referencesRequested"}
                  disabled={isNotAnAdmin}
                />
              }
              label="References Requested"
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Controller
          control={control}
          name={nameof<ExpertsApplyModel>((x) => x.referencesValid)}
          render={(props) => (
            <FormControl component="fieldset" disabled={isNotAnAdmin}>
              <FormLabel id="referencesValid-label" component="legend">
                Are the references for this specialist valid?
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="referencesValid-label"
                name={nameof<ExpertsApplyModel>((x) => x.referencesValid)}
                data-test="radiogroup-referencesValid"
                onChange={(e) => {
                  props.onChange(e.target.value === "true");
                }}
              >
                <FormControlLabel
                  value={true}
                  label="Yes"
                  control={<Radio />}
                  data-test="referencesValid-yes"
                  checked={props.value === true}
                />
                <FormControlLabel
                  value={false}
                  label="No"
                  control={<Radio />}
                  data-test="referencesValid-no"
                  checked={props.value === false}
                />
              </RadioGroup>
            </FormControl>
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name={"analyticalJudgementskills"}
          render={(props) => (
            <FormControl component="fieldset" disabled={isNotAnEvaluator}>
              <FormLabel
                id="AnalyticalJudgementskills-label"
                component="legend"
              >
                In the context of the entire application are you happy with the
                communication and analytical judgement skills.
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="AnalyticalJudgementSkills-label"
                name={"analyticalJudgementSkills"}
                data-test="radiogroup-analyticalJudgementSkills"
                onChange={(e) => {
                  props.onChange(e.target.value === "true");
                }}
              >
                <FormControlLabel
                  value={true}
                  label="Yes"
                  control={<Radio />}
                  data-test="analyticalJudgementSkills-yes"
                  checked={props.value === true}
                />
                <FormControlLabel
                  value={false}
                  label="No"
                  control={<Radio />}
                  data-test="analyticalJudgementSkills-no"
                  checked={props.value === false}
                />
              </RadioGroup>
            </FormControl>
          )}
        />
      </Grid>
    </Grid>
  );
};
